
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Layout, 
  Image as ImageIcon, 
  MonitorPlay, 
  Calendar, 
  MousePointer2,
  CloudSun,
  LayoutGrid
} from 'lucide-react';

const HEADLINES = [
  "Design. Animate. Schedule.",
  "Create content effortlessly.",
  "All-in-one content creation platform."
];

// Reusable animated feature chip
const FeatureChip = ({ text, delay, x, y, icon: Icon, colorClass }: any) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8, y: y + 20 }}
    animate={{ 
      opacity: 1, 
      scale: 1,
      y: [y, y - 8, y],
    }}
    transition={{ 
      opacity: { duration: 0.6, delay },
      scale: { duration: 0.6, delay, type: "spring" },
      y: { duration: 4, delay, repeat: Infinity, ease: "easeInOut" }
    }}
    className="absolute bg-white/90 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white flex items-center gap-3 z-30"
    style={{ left: x, top: y }}
  >
    <div className={`p-1.5 rounded-md ${colorClass}`}>
      <Icon className="w-4 h-4" />
    </div>
    <span className="text-sm font-semibold text-[#1D2739] tracking-tight">{text}</span>
  </motion.div>
);

export const LoginShowcase = () => {
  const [headlineIndex, setHeadlineIndex] = useState(0);

  // Rotate headlines every 4 seconds
  useEffect(() => {
    const textInterval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % HEADLINES.length);
    }, 4000);

    return () => clearInterval(textInterval);
  }, []);

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-[#E0F7FA] to-[#E8F5E9] overflow-hidden hidden lg:flex flex-col items-center justify-center">
      
      {/* Background Decor */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-mint-200 blur-[100px] animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-teal-200 blur-[120px]" />
      </div>

      {/* Header Section */}
      <div className="absolute top-16 left-16 z-40 max-w-md">
        <div className="h-24">
          <AnimatePresence mode="wait">
            <motion.h1
              key={headlineIndex}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-4xl xl:text-5xl font-extrabold text-[#1D2739] leading-tight tracking-tight"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2C9944] to-teal-600">
                {HEADLINES[headlineIndex].split('.')[0]}
              </span>
              {HEADLINES[headlineIndex].includes('.') && (
                <span className="text-[#1D2739]">
                  {HEADLINES[headlineIndex].substring(HEADLINES[headlineIndex].indexOf('.'))}
                </span>
              )}
            </motion.h1>
          </AnimatePresence>
        </div>
      </div>

      {/* Central Interactive Showcase (3D Screen) */}
      <div className="relative w-full max-w-2xl h-[500px] mt-40 flex items-center justify-center perspective-[1200px]">
        
        {/* The "Screen" Canvas */}
        <motion.div
          animate={{ 
            rotateX: [15, 10, 10, 15], 
            rotateY: [-15, -15, -15, -15], 
            scale: [0.9, 1, 1, 0.9], 
            opacity: [0, 1, 1, 0] 
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.15, 0.9, 1]
          }}
          className="relative w-[640px] h-[400px] bg-white rounded-2xl shadow-[0_20px_50px_rgba(44,153,68,0.15)] border-[4px] border-slate-50 overflow-hidden z-10 transform-style-3d group hover:shadow-[0_30px_60px_rgba(44,153,68,0.2)] transition-shadow duration-500"
        >
          {/* Editor Header Mock */}
          <div className="h-10 border-b border-slate-100 bg-slate-50 flex items-center px-4 gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
            </div>
            <div className="ml-4 h-4 w-32 bg-slate-200 rounded-full opacity-50" />
          </div>

          {/* Editor Sidebar Mock */}
          <div className="absolute top-10 left-0 bottom-0 w-16 border-r border-slate-100 bg-white flex flex-col items-center py-4 gap-4 z-20">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500"><Layout className="w-4 h-4" /></div>
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400"><ImageIcon className="w-4 h-4" /></div>
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400"><LayoutGrid className="w-4 h-4" /></div>
          </div>

          {/* The Actual Canvas Area inside Editor */}
          <div className="absolute top-10 left-16 right-0 bottom-0 bg-slate-50/50 p-6 overflow-hidden">
            <div className="bg-white w-full h-full rounded-lg shadow-sm border border-slate-100 relative overflow-hidden">
                <div className="absolute inset-0">
                  {/* Sequence 1: Background Layer Fades In */}
                  <motion.div
                    animate={{ 
                      opacity: [0, 1, 1, 0], 
                      scale: [1.1, 1, 1, 1.1] 
                    }}
                    transition={{ 
                      duration: 6, 
                      repeat: Infinity, 
                      times: [0, 0.15, 0.85, 1],
                      ease: "easeInOut"
                    }}
                    className="absolute inset-0"
                  >
                    {/* Animated Shifting Gradient Pattern */}
                    <motion.div
                      animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 bg-gradient-to-r from-[#E0F7FA] via-indigo-100 to-[#E0F7FA] bg-[length:200%_200%]"
                    />
                  </motion.div>

                  {/* Sequence 2: Text Block Slides In */}
                  <motion.div
                    animate={{ 
                      opacity: [0, 0, 1, 1, 0], 
                      x: [-20, 40, 40, 40, -20], 
                      y: [40, 40, 40, 40, 40] 
                    }}
                    transition={{ 
                      duration: 6, 
                      repeat: Infinity, 
                      times: [0, 0.2, 0.35, 0.85, 1],
                      ease: "backOut" 
                    }}
                    className="absolute w-[200px] p-3 rounded border border-indigo-200 bg-indigo-50/50 opacity-0"
                  >
                    <div className="h-4 w-3/4 bg-indigo-600/80 rounded mb-2" />
                    <div className="h-2 w-full bg-indigo-400/50 rounded" />
                  </motion.div>

                  {/* Sequence 3: Widget Appears */}
                  <motion.div
                    animate={{ 
                      opacity: [0, 0, 1, 1, 0], 
                      scale: [0, 0, 1, 1, 0], 
                      y: [120, 120, 80, 80, 120], 
                      x: [250, 250, 250, 250, 250] 
                    }}
                    transition={{ 
                      duration: 6, 
                      repeat: Infinity, 
                      times: [0, 0.35, 0.5, 0.85, 1],
                      type: "spring", bounce: 0.4
                    }}
                    className="absolute bg-white shadow-xl rounded-xl p-4 flex flex-col items-center justify-center border border-slate-100 opacity-0"
                  >
                    <CloudSun className="w-12 h-12 text-yellow-500 mb-2" />
                    <div className="text-sm font-bold text-slate-700">24°C</div>
                  </motion.div>

                  {/* Mock Cursor Moving */}
                  <motion.div
                    className="absolute z-50 drop-shadow-md"
                    animate={{ 
                      x: [400, 100, 180, 280, 400],
                      y: [250, 150, 120, 200, 250],
                      opacity: [0, 1, 1, 1, 0]
                    }}
                    transition={{ 
                      duration: 6, 
                      repeat: Infinity,
                      ease: "easeInOut",
                      times: [0, 0.15, 0.35, 0.6, 1]
                    }}
                  >
                    <MousePointer2 className="w-6 h-6 text-black fill-black" />
                  </motion.div>
                </div>
            </div>
          </div>
        </motion.div>

        {/* Floating Feature Chips (Around the Screen) */}
        <FeatureChip 
          text="Drag & Drop Editor" 
          delay={0.5} 
          x="-5%" 
          y="5%" 
          icon={Layout} 
          colorClass="bg-indigo-100 text-indigo-600" 
        />
        
        <FeatureChip 
          text="Templates Library" 
          delay={1.0} 
          x="-15%" 
          y="40%" 
          icon={ImageIcon} 
          colorClass="bg-blue-100 text-blue-600" 
        />

        <FeatureChip 
          text="Widgets Integration" 
          delay={1.5} 
          x="75%" 
          y="15%" 
          icon={LayoutGrid} 
          colorClass="bg-yellow-100 text-yellow-600" 
        />

        <FeatureChip 
          text="Live Preview" 
          delay={2.0} 
          x="85%" 
          y="65%" 
          icon={MonitorPlay} 
          colorClass="bg-rose-100 text-rose-600" 
        />

        <FeatureChip 
          text="Schedule Content" 
          delay={2.5} 
          x="10%" 
          y="85%" 
          icon={Calendar} 
          colorClass="bg-emerald-100 text-emerald-600" 
        />

      </div>
    </div>
  );
};
